import React, { useState, useContext } from "react"
import ConnectedCalendar from "./ConnectedCalendar"
import EditCalendarSettings from "./EditCalendarSettings"
import useLoading from "../../../hooks/useLoading"
import { Button } from "../../ui/button"
import { Badge } from "../../ui/badge"
import { CalendarRequests } from "../../../api/app.service"
import { cn } from "../../../utils/cn"
import { CommonFunctionCtx } from "../../../context/commonFunctionContext"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/dialog"
import {
  SvgApple,
  SvgChevronDown,
  SvgGoogleCalendar,
  SvgOffice365Calendar,
  SvgOutlookCalendar,
  SvgPlus,
} from "../../icons"
import { useToast } from "../../../hooks/useToast"
import { UserCtx } from "../../../context/userContext"

interface Provider {
  Image: (props: React.SVGProps<SVGSVGElement>) => JSX.Element
  name: string
  label: string
  button?: string
}

const providers: Provider[] = [
  { Image: SvgGoogleCalendar, name: "google", label: "Google Calendar" },
  { Image: SvgOffice365Calendar, name: "office365", label: "Office365" },
  {
    Image: SvgOutlookCalendar,
    name: "live_connect",
    label: "Outlook",
    button: "Outlook",
  },
  { Image: SvgApple, name: "apple", label: "Apple iCloud" },
]

interface Props {
  profile: any
  profileCount: any
  isLast?: boolean
  fetchUserCalendars: any
}

const ConnectedCalendarProfile: React.FC<Props> = ({
  profile,
  profileCount,
  fetchUserCalendars,
  isLast,
}) => {
  const [profileExpanded, setProfileExpanded] = useState<boolean>(false)
  const [editCalendarSettings, setEditCalendarSettings] = useState<any>({
    show: false,
    calendar: {},
  })
  const { provider_service, calendar_name, cronofy_profile_id } = profile
  const { user } = useContext(UserCtx)
  const { updateNextStepsData } = useContext(CommonFunctionCtx)
  const { startLoading, stopLoading } = useLoading()
  const { toast } = useToast()

  const isPrimary = profile?.calendars?.some(
    (calendar: any) => calendar.is_primary
  )

  const typeCapitalized = () => {
    let service = provider_service
    if (service === "outlook_com") service = "outlook"
    if (service === "gsuite") service = "google"
    return service.charAt(0).toUpperCase() + service.slice(1)
  }

  const getCalendarLogo = () => {
    if (provider_service === "gsuite" || "google") return <SvgGoogleCalendar />
    if (provider_service === "office365") return <SvgOffice365Calendar />
    if (provider_service === "outlook_com") return <SvgOutlookCalendar />
    if (provider_service === "apple") return <SvgApple />
  }

  const disconnectCalendar = () => {
    if (user) {
      startLoading()
      CalendarRequests.deleteCalendar({
        user_id: parseInt(user.user_id),
        profile_count: profileCount,
        cronofy_profile_id,
      })
        .then(() => {
          toast.success("Deleted calendar successfully")
          fetchUserCalendars()
          stopLoading()
          updateNextStepsData(false)
        })
        .catch((ex: any) => {
          console.error(ex)
          toast.error(ex?.response?.data?.message)
          stopLoading()
        })
    }
  }

  const startConnectCalendar = (provider_name: string) => {
    const redirectUri = `${process.env.REACT_APP_BASE_URL}/account-settings/calendars`
    const url = `https://app.cronofy.com/oauth/authorize?response_type=code&client_id=${process.env.REACT_APP_CRONOFY_CLIENT_ID}&redirect_uri=${redirectUri}&scope=read_write&provider_name=${provider_name}&avoid_linking=true`
    window.location.href = url
  }

  return (
    <>
      {editCalendarSettings?.show && (
        <EditCalendarSettings
          calendar={editCalendarSettings.calendar}
          setEditCalendarSettings={setEditCalendarSettings}
          profile={profile}
          fetchUserCalendars={fetchUserCalendars}
        />
      )}

      <div className="mx-auto flex w-full max-w-screen-xl flex-col">
        <div
          className={cn(
            "flex flex-col",
            isPrimary ? "p-10" : "pt-0 pb-10 px-10"
          )}
        >
          {" "}
          {isPrimary && (
            <>
              <div className="w-full flex items-center justify-between text-left text-xl font-bold">
                <span>Connected Accounts</span>

                <Dialog>
                  <DialogTrigger asChild>
                    <Button className="hidden sm:flex items-center gap-1">
                      New Calendar <SvgPlus />
                    </Button>
                  </DialogTrigger>

                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="text-center">
                        Select Your Calendar
                      </DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      {providers.map(({ Image, name, label, button }) => (
                        <div
                          key={label}
                          className="flex flex-row p-4 lg:p-6 gap-6 border border-1 border-border rounded-lg justify-between sm:items-center"
                        >
                          <div className="flex gap-4 items-center font-bold text-lg">
                            <Image className="w-8 h-8 sm:w-12 sm:h-12" />
                            {label}
                          </div>
                          <Button
                            onClick={() => startConnectCalendar(name)}
                            variant="secondary"
                          >
                            <span>
                              Sign in{" "}
                              <span className="hidden sm:inline">
                                {button ?? name}
                              </span>
                            </span>
                          </Button>
                        </div>
                      ))}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="bg-grayMist w-full h-[1px] mt-2 mb-2"></div>
            </>
          )}
          <div className="flex w-full flex-col items-center md:flex-row md:gap-0">
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center gap-[12px] md:gap-[24px]">
                {getCalendarLogo()}

                <div className="flex flex-col gap-[2px] md:gap-[4px]">
                  <p className="whitespace-nowrap text-[16px] font-bold">
                    {`${typeCapitalized()} Calendar`}
                  </p>
                  <p className="text-graySlate whitespace-nowrap text-base md:text-[16px]">
                    {calendar_name}
                  </p>
                </div>

                {isPrimary && <Badge>Primary</Badge>}
              </div>
            </div>

            <div className="flex w-full flex-fow gap-5 md:flex-row-reverse">
              <div className="relative w-full sm:w-auto group">
                <Button
                  onClick={disconnectCalendar}
                  variant="secondary"
                  disabled={isPrimary}
                  className="w-full sm:w-auto"
                >
                  Disconnect
                </Button>

                {isPrimary && (
                  <div className="absolute top-[-56px] left-1/2 z-20 w-[240px] -translate-x-1/2 rounded-md border bg-white px-4 py-3 text-sm text-gray-800 shadow-md hidden group-hover:block">
                    You can’t disconnect your primary calendar. Please set
                    another calendar as primary before disconnecting this one.
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                onClick={() => setProfileExpanded(!profileExpanded)}
                className="w-1/2 sm:w-auto"
              >
                <span className="whitespace-nowrap">View Calendars</span>
                <SvgChevronDown
                  className={cn("transition-transform", {
                    "-scale-y-100": profileExpanded,
                  })}
                />
              </Button>
            </div>
          </div>
          <div className="w-full flex justify-center sm:hidden">
            <Dialog>
              {isLast && (
                <DialogTrigger asChild>
                  <Button className="flex items-center gap-1 w-full mt-2">
                    New Calendar <SvgPlus />
                  </Button>
                </DialogTrigger>
              )}

              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="text-center">
                    Select Your Calendar
                  </DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  {providers.map(({ Image, name, label, button }) => (
                    <div
                      key={label}
                      className="flex flex-row p-4 lg:p-6 gap-6 border border-1 border-border rounded-lg justify-between sm:items-center"
                    >
                      <div className="flex gap-4 items-center font-bold text-lg">
                        <Image className="w-8 h-8 sm:w-12 sm:h-12" />
                        {label}
                      </div>
                      <Button
                        onClick={() => startConnectCalendar(name)}
                        variant="secondary"
                      >
                        <span>
                          Sign in{" "}
                          <span className="hidden sm:inline">
                            {button ?? name}
                          </span>
                        </span>
                      </Button>
                    </div>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          </div>
          {profileExpanded && (
            <>
              <div className="bg-grayCloud h-[1px] w-full"></div>
              <div className="flex flex-col pt-4">
                <div className="flex flex-col gap-4">
                  {profile?.calendars?.map((calendar: any, index: number) => (
                    <ConnectedCalendar
                      setEditCalendarSettings={setEditCalendarSettings}
                      calendar={calendar}
                      key={index}
                      fetchUserCalendars={fetchUserCalendars}
                    />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default ConnectedCalendarProfile
